import { getSessionToken } from '../auth-client';
import { apiFetch } from '../api-client';

export type ImportKind =
  | 'salary-config'
  | 'bank-software-nv'
  | 'bank-software-tts'
  | 'bank-group'
  | 'payroll-batch'; // Sinh Payroll DRAFT + PayrollLine cho kỳ hiện tại (không update NV)

export interface FieldChange {
  from: unknown;
  to: unknown;
}

export interface ParsedRowData {
  agreedSalary?: number | null;
  salaryType?: 'GROSS' | 'NET';
  socialInsuranceBase?: number | null;
  bankAccount?: string | null;
  bankName?: string | null;
  bankBranch?: string | null;
  positionHint?: string | null;
  departmentHint?: string | null;
  note?: string | null;
}

export interface MatchedRow {
  rowIndex: number;
  rawName: string;
  employeeId: string;
  employeeCode: string;
  employeeName: string;
  data: ParsedRowData;
  changes: Record<string, FieldChange>;
}

export interface UnmatchedRow {
  rowIndex: number;
  rawName: string;
  reason: 'no-match' | 'multi-match' | 'invalid';
  message: string;
  data: ParsedRowData;
}

export interface ImportPreview {
  kind: ImportKind;
  sheetName: string;
  totalRows: number;
  matched: MatchedRow[];
  unmatched: UnmatchedRow[];
}

export interface CommitResult {
  updated: number;
  skipped: number;
  errors: Array<{ employeeId: string; message: string }>;
  /** Chỉ có khi kind='payroll-batch' */
  payrollId?: string;
  payrollCode?: string;
}

async function uploadPreview(kind: ImportKind, file: File): Promise<ImportPreview> {
  const token = getSessionToken();
  const fd = new FormData();
  fd.append('file', file);
  const res = await fetch(`/api/salary-import/preview?kind=${encodeURIComponent(kind)}`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: fd,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Preview fail: ${res.status} ${text}`);
  }
  return (await res.json()) as ImportPreview;
}

export type TemplateType = 'salary-config' | 'bank-software' | 'bank-group';

/** Map ImportKind (dropdown menu) → TemplateType (file template có thể dùng chung). */
export function templateTypeForKind(kind: ImportKind): TemplateType {
  if (kind === 'salary-config') return 'salary-config';
  if (kind === 'bank-group') return 'bank-group';
  return 'bank-software';
}

async function downloadTemplate(type: TemplateType): Promise<void> {
  const token = getSessionToken();
  const res = await fetch(`/api/salary-import/template?type=${encodeURIComponent(type)}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Tải template fail: ${res.status} ${text}`);
  }
  // Ưu tiên lấy tên file từ Content-Disposition (server đã set), fallback theo type.
  const cd = res.headers.get('content-disposition') || '';
  const nameMatch = cd.match(/filename="?([^";]+)"?/i);
  const fileName = nameMatch?.[1] || `template-${type}.xlsx`;
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

export const salaryImportApi = {
  preview: uploadPreview,
  commit: (kind: ImportKind, rows: Array<{ employeeId: string; data: ParsedRowData }>) =>
    apiFetch<CommitResult>('/api/salary-import/commit', {
      method: 'POST',
      body: JSON.stringify({ kind, rows }),
    }),
  downloadTemplate,
};
